import {
  Chat16,
  CheckmarkOutline16,
  Close20,
  EarthFilled16,
  Menu20,
  Notification16,
  OverflowMenuHorizontal16,
} from '@carbon/icons-react';
import { Tooltip } from 'antd';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import tw from 'tailwind-styled-components';
import { CustomerPortalButtonType } from 'api/customer-portal/theme-api';
import {
  CustomerPortalPageLight,
  CustomerPortalPageType,
} from 'api/customer-portal/types';
import { AccountId, UserId } from 'api/models';
import { Project } from 'api/project';
import { ReactComponent as ProjectSwitcherIconSvg } from 'assets/svgs/project-switcher.svg';
import {
  CpBody01Primary,
  CpBody0aPrimary,
} from 'components/atoms/customer-portal/typography';
import { useAccount } from 'hooks/use-account';
import { useIsResponsive, useIsTablet } from 'hooks/use-media-query';
import { useUser } from 'hooks/use-user';
import { usePending } from 'stores/notifications';
import { initialize } from 'stores/notifications/use-pending';
import { APPROVALS_AND_CSAT_PAGE, NOTIFICATIONS_PAGE } from '../slice';
import { useApprovalsAndCsatCount } from './cp-approvals-and-csat/cp-approval-and-csat-icon';
import { CpApprovalsAndCsat } from './cp-approvals-and-csat/cp-approvals-and-csat';
import { CpButton } from './cp-button';
import { CpMenu } from './modal/cp-menu';
import { CpPopover } from './modal/cp-popover';
import { CpNotificationsContent } from './widgets/cp-notifications-content';

export interface CpNavBarProps {
  pages: CustomerPortalPageLight[];
  onPageClick: (p: CustomerPortalPageLight) => void;
  title: React.ReactNode;
  activePageRefId?: string;
  projects: Project[];
  onProjectChange: (projectId: number) => void;
  disableProjectSwitcher?: boolean;
  disableNotifications?: boolean;
  selectedProject?: Project;
  onSlackIntegrationClick?: () => void;
  onNotificationSettingsClick?: () => void;
  disableApprovalsAndCsat?: boolean;
  dontShowNotificationSettings?: boolean;
  showInviteButton?: boolean;
  onShareClick?: () => void;
  dontShowOverview?: boolean;
}

export const CpRouteConstants = {
  home: '/',
  notifications: '/notifications',
  accountOverview: '/overview',
  projects: '/projects',
  chat: '/chats',
} as const;

const ProjectSwitcherButton = tw.button`
      cp-flex
      cp-items-center
      cp-justify-center
      cp-cursor-pointer
      cp-bg-transparent
      cp-text-[var(--white-100)]
      cp-border-none
      cp-outline-none
      md:cp-mr-[var(--cp-spacing-05)]
      cp-h-full
`;
const MoreTabsButton = tw.button`
      cp-flex
      cp-items-center
      cp-justify-center
      cp-cursor-pointer
      cp-bg-transparent
      cp-text-[var(--white-100)]
      cp-border-none
      cp-outline-none
      cp-h-full
      cp-p-[var(--spacing-04)]
`;

const ProjectSwitcherTrigger = styled.span`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${CpBody01Primary} {
    color: white !important;
    margin-right: var(--cp-spacing-03);
  }
  @media (max-width: 480px) {
    flex-direction: row-reverse;
    margin-left: var(--cp-spacing-04);
    gap: var(--cp-spacing-03);
  }
`;

const ProjectSwitcherIcon: React.FC = () => (
  <ProjectSwitcherButton>
    <ProjectSwitcherIconSvg />
  </ProjectSwitcherButton>
);

const CpNavbarItemLabelOrIcon: React.FC<{
  page: CustomerPortalPageLight;
}> = ({ page }) => {
  if (!page) {
    return <></>;
  }
  const { pageType, pageName } = page;

  if (page === NOTIFICATIONS_PAGE) {
    const content = (
      <>
        <Notification16 color="var(--cp-white)" />
        <p className="md:cp-hidden md:cp-w-0 cp-mb-0">{pageName}</p>
      </>
    );

    return content;
  }
  if (page === APPROVALS_AND_CSAT_PAGE) {
    const content = (
      <>
        <CheckmarkOutline16 />
        <p className="md:cp-hidden md:cp-w-0 cp-mb-0">{pageName}</p>
      </>
    );

    return content;
  }

  switch (pageType) {
    case CustomerPortalPageType.Home:
      return <p className="cp-mb-0">{pageName}</p>;

    case CustomerPortalPageType.Chat:
      return (
        <>
          <Chat16 />
          <p className="md:cp-hidden md:cp-w-0 cp-mb-0">{pageName}</p>
        </>
      );

    case CustomerPortalPageType.AccountOverview:
      return (
        <>
          <EarthFilled16 />
          <p className="md:cp-hidden md:cp-w-0 cp-mb-0">{pageName}</p>
        </>
      );

    default:
      return <p className="cp-mb-0">{pageName}</p>;
  }
};

// const TitleWrapper = tw.div`rl-cp-nav-bar-title-wrapper cp-w-auto cp-h-full cp-p-[var(--spacing-05)__var(--spacing-06)] cp-cursor-pointer md:cp-pl-[var(--cp-spacing-06)] cp-pl-[var(--cp-spacing-05)] lg:cp-pr-0`;
const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--black-100);
  position: sticky;
  top: 0;
  z-index: 100;
  margin-left: auto;
  margin-right: auto;
  padding-left: calc(calc(100% - 1440px) / 2);
  padding-right: calc(calc(100% - 1440px) / 2);
  border-bottom: 1px solid var(--cp-border-subtle-inverse);
`;

const LgRoutesList = tw.ul<{ end?: boolean }>`
    rl-cp-routes-wrapper
    cp-hidden
    md:cp-flex
    ${(props) => (props?.end ? 'cp-justify-end' : 'cp-justify-start')}
    cp-pl-0
    cp-items-center
    cp-h-[58px]
    cp-w-full
    cp-mb-0
`;
const Count = styled.div`
  background: var(--cp-red-60);
  color: var(--cp-white);
  border-radius: 24px;
  padding: 0 var(--cp-spacing-03);
`;
const SmallCount = styled(Count)`
  font-size: var(--cp-body-0a-font-size);
  line-height: var(--line-height-caption-01); ;
`;

export const NavItem = styled.li<{ isActive?: boolean }>`
  height: 58px;
  ${(props) =>
    props?.isActive
      ? css`
          @media (min-width: 768px) {
            border-color: var(--cp-blue-60);
            border-style: solid;
          }
          @media (max-width: 480px) {
            position: relative;
            &::after {
              content: '';
              height: 100%;
              width: 4px;
              top: 0;
              position: absolute;
              right: 0;
              background-color: var(--cp-gray-20);
            }
          }
        `
      : css`
          @media (min-width: 768px) {
            border-color: transparent;
            border-style: solid;
          }
        `};
  color: var(--cp-white-100);
  border-width: 0;
  ${SmallCount} {
    margin-left: var(--cp-spacing-02);
  }
  @media (min-width: 768px) {
    border-bottom-width: 4px;
    padding-top: var(--cp-spacing-05);
  }
  cursor: pointer;
  @media (min-width: 1024px) {
    margin-right: var(--cp-spacing-05);
  }
  @media (min-width: 768px) {
    margin-right: var(--cp-spacing-03);
  }
  display: flex;
  align-items: center;
  justify-content: flex-end;
  @media (min-width: 1024px) {
    justify-content: center;
  }
  @media (min-width: 768px) {
    width: auto;
  }
  padding: var(--cp-spacing-04);
  border-bottom: 1px solid var(--cp-gray-20);
  @media (min-width: 1024px) {
    &:hover {
      border-width: 0;
      border-bottom-width: 4px;
      border-color: var(--cp-gray-20);
      border-style: solid;
    }
  }
  white-space: nowrap;
  @media (max-width: 480px) {
    width: 100%;
    border-bottom-color: var(--cp-border-strong-inverse);
  }
`;
const SpacerInMenu = styled.li`
  height: var(--cp-spacing-07);
`;

const SlideInAnimation = css`
  transform: translate(var(--tw-translate-x), var(--tw-translate-y))
    rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y))
    scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
  transition-duration: 500ms;
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
`;

const ShareButton = styled(CpButton)<{ hamMenuVisible?: boolean }>`
  margin-right: var(--cp-spacing-06);
  --tw-translate-x: 0;
  transition-duration: 0s;

  @media (max-width: 480px) {
    ${(props) =>
      props.hamMenuVisible
        ? css`
            display: flex;
            --tw-translate-x: 0;
          `
        : css`
            --tw-translate-x: -10000px;
          `};
    ${SlideInAnimation};
    position: fixed;
    z-index: 10000000;
    width: calc(100% - calc(var(--cp-spacing-05) * 2));
    bottom: var(--cp-spacing-06);
    left: var(--cp-spacing-05);
    &:after {
      content: '';
      position: absolute;
      bottom: calc(-1 * var(--cp-spacing-06));
      height: var(--cp-spacing-06);
      width: calc(100% + var(--cp-spacing-05));
      --tw-bg-opacity: 1;
      background-color: rgb(0 0 0 / var(--tw-bg-opacity));
      left: calc(calc(var(--cp-spacing-05) / 2) - 1);
    }
  }
`;

const SmRoutesList = styled.div<{ visible: boolean }>`
  ${(props) =>
    props.visible
      ? css`
          display: flex;
          --tw-translate-x: 0;
        `
      : css`
          --tw-translate-x: -10000px;
        `};
  ${SlideInAnimation};
  --tw-bg-opacity: 1;
  background-color: rgb(0 0 0 / var(--tw-bg-opacity));
  justify-content: flex-start;
  align-items: flex-end;
  flex-direction: column;
  width: 100%;
  margin-bottom: 0;
  z-index: 10000000;
  left: 0;
  top: 65px;
  position: fixed;
  height: calc(100% - 65px);
  overflow-y: auto;

  ${NavItem} {
    flex-direction: row-reverse;
    justify-content: flex-start;
    gap: var(--cp-spacing-03);
    .cp-notification-icon {
      width: unset;
    }
  }
  > span {
    width: 100%;
    > span {
      width: 100%;
    }
  }
  @media (min-width: 768px) {
    display: none;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  &::-webkit-scrollbar-thumb {
    display: none;
  }

  &::-webkit-scrollbar-track {
    display: none;
  }
`;

export const NavItemWithCount = styled(NavItem)<{
  showCount: boolean;
  count: number;
}>`
  position: relative;
  &::after {
    content: '${({ count }) => count}';
    position: absolute;
    top: 14px;
    right: 2px;
    background: var(--cp-red-60);
    color: var(--cp-white);
    border-radius: 100%;
    height: 1rem;
    width: 1rem;

    align-items: center;
    justify-content: center;
    font-size: 12px;
    display: ${({ showCount }) => (showCount ? 'flex' : 'none')};
  }
`;

const HamMenuLabel = styled(CpBody0aPrimary)`
  color: var(--white-100);
  text-transform: capitalize;
`;

const HamMenuButton = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: transparent;
  color: var(--white-100);
  border: none;
  outline: none;
  padding: 0 var(--spacing-05);
  height: 64px;
  flex-direction: column;
  gap: var(--cp-spacing-03);
  &:hover {
    background-color: var(--cp-gray-80-hover);
  }
  ${({ isActive }) =>
    isActive
      ? css`
          background-color: var(--cp-gray-80-hover);
        `
      : ''};
  @media (min-width: 768px) {
    display: none;
  }
`;

export const CpNavBarRenderer: React.FC<CpNavBarProps> = ({
  pages,
  onPageClick,
  activePageRefId,
  projects,
  onProjectChange,
  disableProjectSwitcher,
  selectedProject,
  disableNotifications,
  onSlackIntegrationClick,
  onNotificationSettingsClick,
  disableApprovalsAndCsat,
  dontShowNotificationSettings,
  showInviteButton,
  onShareClick,
  dontShowOverview,
}) => {
  const isResponsive = useIsResponsive();
  const isTab = useIsTablet();
  const [isHamMenuVisible, setHamMenuVisiblity] = useState(false);
  const [isNotificationsVisible, setNotificationsVisibility] = useState(false);
  const onNavItemClick = useCallback(
    (page: CustomerPortalPageLight) => {
      onPageClick(page);
      setHamMenuVisiblity(false);
    },
    [onPageClick],
  );

  const filteredPages = useMemo(
    () =>
      pages.filter(
        (e) =>
          e?.pageType === CustomerPortalPageType.Custom ||
          e?.pageType === CustomerPortalPageType.Home,
      ),
    [pages],
  );

  const pagesLookup = useMemo(() => _.keyBy(pages, 'routePath'), [pages]);

  /* overview page */
  const overviewPage = useMemo(
    () => pagesLookup[CpRouteConstants.accountOverview],
    [pagesLookup],
  );

  /* chat page */
  const chatPage = useMemo(
    () => pagesLookup[CpRouteConstants.chat],
    [pagesLookup],
  );

  const projectSwitcherComponent = useMemo(
    () =>
      disableProjectSwitcher || projects?.length < 2 ? (
        isResponsive ? (
          <ProjectSwitcherTrigger data-cy="cp.project_switcher.project_name">
            <CpBody01Primary>
              {selectedProject?.projectName?.slice?.(
                0,
                Math.min(selectedProject?.projectName?.length, 20),
              )}
              {selectedProject && selectedProject?.projectName?.length > 20 ? (
                '...'
              ) : (
                <></>
              )}
            </CpBody01Primary>
          </ProjectSwitcherTrigger>
        ) : (
          <ProjectSwitcherTrigger data-cy="cp.project_switcher.project_name">
            <CpBody01Primary className="cp-hidden lg:cp-flex">
              {selectedProject?.projectName?.slice?.(
                0,
                Math.min(selectedProject?.projectName?.length, 20),
              )}
              {selectedProject && selectedProject?.projectName?.length > 20 ? (
                '...'
              ) : (
                <></>
              )}
            </CpBody01Primary>{' '}
            <ProjectSwitcherIcon />
          </ProjectSwitcherTrigger>
        )
      ) : (
        <CpPopover
          placement="bottomLeft"
          closeOnSelect
          trigger={['click', 'hover']}
          data-cy="cp.preview_mode.project_switcher_wrapper"
          content={
            <CpMenu
              data-cy="cp.preview_mode.project_switcher"
              selectedKeys={
                selectedProject ? [selectedProject?.projectId?.toString()] : []
              }
            >
              {_.sortBy(projects ?? [], (p) => p?.projectId)?.map((e) => (
                <CpMenu.Item
                  data-cy={`cp.preview_mode.project_switcher.${e?.projectName}`}
                  onClick={() => onProjectChange(e?.projectId)}
                  key={e?.projectId?.toString()}
                >
                  {isResponsive
                    ? e?.projectName
                    : e?.projectName?.length > 25
                    ? `${e?.projectName?.slice(0, 25)}...`
                    : e?.projectName}
                </CpMenu.Item>
              ))}
            </CpMenu>
          }
        >
          <ProjectSwitcherTrigger data-cy="cp.project_switcher.project_name">
            <CpBody01Primary className="md:cp-hidden sm:cp-flex lg:cp-flex">
              {selectedProject?.projectName?.slice?.(
                0,
                Math.min(selectedProject?.projectName?.length, 20),
              )}
              {selectedProject && selectedProject?.projectName?.length > 20 ? (
                '...'
              ) : (
                <></>
              )}
            </CpBody01Primary>
            <ProjectSwitcherIcon />
          </ProjectSwitcherTrigger>
        </CpPopover>
      ),
    [
      disableProjectSwitcher,
      isResponsive,
      onProjectChange,
      projects,
      selectedProject,
    ],
  );
  const approvalsAndCsatsCount = useApprovalsAndCsatCount();

  const approvalsAndCsatComponent = useMemo(() => {
    const icon = isResponsive ? (
      <NavItem>
        <CpNavbarItemLabelOrIcon page={APPROVALS_AND_CSAT_PAGE} />
        {approvalsAndCsatsCount > 0 ? (
          <Count>{approvalsAndCsatsCount}</Count>
        ) : (
          <></>
        )}
      </NavItem>
    ) : (
      <NavItem>
        <CpNavbarItemLabelOrIcon page={APPROVALS_AND_CSAT_PAGE} />
        {approvalsAndCsatsCount > 0 ? (
          <SmallCount>{approvalsAndCsatsCount}</SmallCount>
        ) : (
          <></>
        )}
      </NavItem>
    );

    if (disableApprovalsAndCsat) {
      return icon;
    }

    return <CpApprovalsAndCsat>{icon}</CpApprovalsAndCsat>;
  }, [approvalsAndCsatsCount, disableApprovalsAndCsat, isResponsive]);

  const firstHalf = useMemo(() => {
    if (isTab) {
      if (filteredPages?.length > 2) {
        return filteredPages?.slice(0, 2);
      }
    }

    if (filteredPages?.length > 6) {
      return filteredPages.slice(0, 6);
    }

    return filteredPages;
  }, [filteredPages, isTab]);

  const secondHalf = useMemo(() => {
    if (isTab) {
      if (filteredPages?.length > 2) {
        return filteredPages?.slice(2);
      }
    }

    if (filteredPages?.length > 6) {
      return filteredPages.slice(6);
    }

    return [];
  }, [filteredPages, isTab]);

  const [hasPending] = usePending();
  const {
    user: { userId },
  } = useUser();
  const { accountId } = useAccount();

  useEffect(() => {
    initialize(UserId(userId), AccountId(accountId));
  }, [accountId, userId]);

  const notificationsContent = useMemo(() => {
    const content = isResponsive ? (
      <NavItem data-cy="cp.preview_mode.nav_bar.notificatons_page">
        <CpNavbarItemLabelOrIcon page={NOTIFICATIONS_PAGE} />
        {hasPending > 0 ? <Count>{hasPending}</Count> : <></>}
      </NavItem>
    ) : (
      <Tooltip title={NOTIFICATIONS_PAGE.pageName}>
        <NavItem data-cy="cp.preview_mode.nav_bar.notificatons_page">
          <CpNavbarItemLabelOrIcon page={NOTIFICATIONS_PAGE} />
          {hasPending > 0 ? <SmallCount>{hasPending}</SmallCount> : <></>}
        </NavItem>
      </Tooltip>
    );

    if (disableNotifications) {
      return content;
    }

    return (
      <CpPopover
        trigger={['click']}
        visible={isNotificationsVisible}
        onVisibleChange={(visible) => setNotificationsVisibility(visible)}
        content={
          <CpNotificationsContent
            dontShowNotificationSettings={dontShowNotificationSettings}
            onNotificationSettingsClick={onNotificationSettingsClick}
            onSlackIntegrationClick={onSlackIntegrationClick}
            closePopover={() => setNotificationsVisibility(false)}
          />
        }
        getPopupContainer={() =>
          document.getElementById('cp-modal-root') as HTMLElement
        }
      >
        <div>{content}</div>
      </CpPopover>
    );
  }, [
    disableNotifications,
    dontShowNotificationSettings,
    hasPending,
    isNotificationsVisible,
    isResponsive,
    onNotificationSettingsClick,
    onSlackIntegrationClick,
  ]);

  return (
    <Nav className="rl-cp-nav-bar-wrapper">
      <LgRoutesList className="cp-ml-[var(--cp-spacing-03)]">
        {firstHalf.map((e) => {
          return (
            <NavItem
              data-cy={`cp.preview_mode.nav_bar.${e?.pageName}`}
              key={e?.pageReferenceId}
              isActive={activePageRefId === e?.pageReferenceId}
              onClick={() => onNavItemClick(e)}
            >
              <CpNavbarItemLabelOrIcon page={e} />
            </NavItem>
          );
        })}
        {isResponsive ? (
          <></>
        ) : secondHalf?.length ? (
          <CpPopover
            content={
              <CpMenu
                selectedKeys={
                  activePageRefId ? [activePageRefId?.toString()] : []
                }
              >
                {secondHalf.map((e) => (
                  <CpMenu.Item
                    data-cy={`cp.preview_mode.nav_bar.${e?.pageName}`}
                    key={e?.pageReferenceId}
                    onClick={() => onNavItemClick(e)}
                  >
                    {e?.pageName}
                  </CpMenu.Item>
                ))}
              </CpMenu>
            }
          >
            <NavItem data-cy="cp.preview_mode.nav_bar.overflowmenu">
              <MoreTabsButton>
                <OverflowMenuHorizontal16 />
              </MoreTabsButton>
            </NavItem>
          </CpPopover>
        ) : (
          <></>
        )}
      </LgRoutesList>
      <LgRoutesList end>
        {!dontShowOverview && overviewPage ? (
          <Tooltip title={overviewPage?.pageName}>
            <NavItem
              data-cy="cp.preview_mode.nav_bar.overview_page"
              isActive={activePageRefId === overviewPage?.pageReferenceId}
              onClick={() => onNavItemClick(overviewPage)}
            >
              <CpNavbarItemLabelOrIcon page={overviewPage} />
            </NavItem>
          </Tooltip>
        ) : (
          <></>
        )}

        {chatPage ? (
          <Tooltip title={chatPage?.pageName}>
            <NavItem
              data-cy="cp.preview_mode.nav_bar.chat_page"
              isActive={activePageRefId === chatPage?.pageReferenceId}
              onClick={() => onNavItemClick(chatPage)}
            >
              <CpNavbarItemLabelOrIcon page={chatPage} />
            </NavItem>
          </Tooltip>
        ) : (
          <></>
        )}

        {!isResponsive ? notificationsContent : <></>}

        {!isResponsive ? approvalsAndCsatComponent : <></>}
        {!isResponsive ? projectSwitcherComponent : <></>}
      </LgRoutesList>

      <SmRoutesList visible={isHamMenuVisible}>
        {filteredPages.map((e) => (
          <NavItem
            data-cy={`cp.preview_mode.nav_bar.${e?.pageName}`}
            key={e?.pageReferenceId}
            isActive={activePageRefId === e?.pageReferenceId}
            onClick={() => onNavItemClick(e)}
          >
            <CpNavbarItemLabelOrIcon page={e} />
          </NavItem>
        ))}

        <SpacerInMenu />
        {(dontShowOverview ? [] : [overviewPage])
          .concat([chatPage])
          .filter((e) => !!e)
          .map((e) => (
            <NavItem
              isActive={activePageRefId === e?.pageReferenceId}
              key={e?.pageReferenceId}
              onClick={() => onNavItemClick(e)}
            >
              <CpNavbarItemLabelOrIcon page={e} />
            </NavItem>
          ))}
        {isResponsive ? notificationsContent : <></>}
        {isResponsive ? approvalsAndCsatComponent : <></>}
        {showInviteButton ? (
          <>
            <SpacerInMenu />
            <SpacerInMenu />
            <SpacerInMenu />
            <SpacerInMenu />
          </>
        ) : (
          <></>
        )}
      </SmRoutesList>
      {isResponsive ? projectSwitcherComponent : <></>}
      <HamMenuButton
        onClick={() => {
          setHamMenuVisiblity((p) => !p);
        }}
        isActive={isHamMenuVisible}
      >
        {isHamMenuVisible ? (
          <>
            <Close20 />
            <HamMenuLabel>
              <FormattedMessage id="close" />
            </HamMenuLabel>
          </>
        ) : (
          <>
            <Menu20 />
            <HamMenuLabel>
              <FormattedMessage id="menu" />
            </HamMenuLabel>
          </>
        )}
      </HamMenuButton>
      {showInviteButton ? (
        <ShareButton
          data-cy="cp.preview_mode.nav_bar.share_button"
          onClick={onShareClick}
          cpType={CustomerPortalButtonType.Primary}
          hamMenuVisible={isHamMenuVisible}
        >
          <FormattedMessage id="share" />
        </ShareButton>
      ) : (
        <></>
      )}
    </Nav>
  );
};
